/**
 * Represents a single feature object from the Feature Flags service.  Methods
 * that retrieve features from the service return read-only instances of
 * Feature, as they reflect the authoritative feature properties.  Callers
 * wishing to locally-modify feature data should use clone() to replicate an
 * existing read-only feature or new Feature() to create a non-authoritative
 * instance.
 */

import { isInsideSas } from './enabled';

import {
  addFeatureReleaseToggle,
  getFeature,
  getFeatures,
  getAllFeatures,
  activateFeature,
  deactivateFeature,
  setFeatureDefaultValue,
  addFeatureUserException,
  addFeatureGroupException,
  removeFeatureUserException,
  removeFeatureGroupException,
  resetFeatureToggle,
  deleteFeatureToggle,
  canCreateFeatureOnTheFly,
  createFeatureOnTheFly,
  enableFeatureForCurrentUser,
  disableFeatureForCurrentUser,
  ReadonlyFeatureDict,
  toggleFeatureForCurrentUser,
  approveFeature,
  revokeFeature,
} from './features';

/**
 * Expected toggle types.  The service may return other toggle types, and the client should handle
 * them appropriately.
 */
export enum ToggleType {
  None = 'none',
  Release = 'release',
  Experimental = 'experimental',
  Permissioning = 'permissioning',
}

/**
 * Application-related information.
 */
export interface Product {
  appRegistryId: string;
  appName?: string;
  label?: string;
}

export class Feature {
  /**
   * Queries whether the environment is inside SAS.
   * @see {@link isInsideSas}
   * @example
   * ```
   * await isInsideSas = Feature.isInsideSas();
   * ```
   */
  public static async isInsideSas(): Promise<boolean> {
    return isInsideSas();
  }

  /**
   * Acquires a feature definition.
   * @see {@link getFeature}
   * @example
   * ```
   * await feature = Feature.getFeature('MyFeature');
   * ```
   */
  public static async getFeature(featureId: string): Promise<ReadonlyFeature> {
    return getFeature(featureId);
  }

  /**
   * Acquires multiple feature definitions.
   * @see {@link getFeatures}
   * @example
   * ```
   * await featureDict = Feature.getFeatures('MyFeature1', 'MyFeature2');
   * const str = featureDict.MyFeature1.label;
   * ```
   */
  public static async getFeatures<Args extends string[]>(
    ...featureIds: Args
  ): Promise<ReadonlyFeatureDict<Args>> {
    return getFeatures(...featureIds);
  }

  /**
   * Acquire all known features.
   * @see {@link getAllFeatures}
   * @example
   * ```
   * await features = Feature.getAllFeatures('SASDrive');
   * features.forEach((feature) => { ... });
   * ```
   */
  public static async getAllFeatures(appIdFilter?: string): Promise<ReadonlyFeature[]> {
    return getAllFeatures(appIdFilter);
  }

  /**
   * Query whether a user has access to create a feature on-the-fly.
   * @see {@link canCreateFeatureOnTheFly}
   */
  public static async canCreateFeatureOnTheFly(): Promise<boolean> {
    return canCreateFeatureOnTheFly();
  }

  /**
   * Creates a feature on-the-fly.
   * @see {@link createFeatureOnTheFly}
   * @see {@link Feature.canCreateFeatureOnTheFly}
   * @example
   * ```
   * const feature = await createFeatureOnTheFly(
   *   'Sandwiches', 'Sandwiches are divine.', 'XYZZY-001', 'SASDrive');
   * ```
   */
  public static async createFeatureOnTheFly(
    featureId: string,
    description: string,
    issueTracking: string,
    productId?: string
  ): Promise<ReadonlyFeature> {
    return createFeatureOnTheFly(featureId, description, issueTracking, productId);
  }

  /** Feature identifier.  Not meant to be shown to the user. */
  public id: string;

  /** Identifier label, to be shown to the user.  Will be undefined outside of SAS. */
  public idLabel: string | undefined;

  /** Feature label, to be shown to the user. */
  public label: string;

  /** Optional feature description. */
  public description: string | undefined;

  /** Array of one or more toggle types.  Typically of type ToggleType, but not guaranteed. */
  public toggleTypes: Array<ToggleType | string>;

  /** Optional id of user who created this feature. Typically a service's internal id. */
  public createdBy: string | undefined;

  /** Optional date and time when feature was created. */
  public creationDate: Date | undefined;

  /** Array of one or more tracking ids or URIs.  Typically Jira story ids. */
  public issueTracking: string[];

  /** Array of zero or more associated applications.  Typically a length of 1. */
  public products: Product[];

  /** Whether one of the toggles was dynamically created rather than bootstrapped by a service. */
  public isDynamicallyAddedToggle = false;

  /** Whether approval is required for this feature.  True for experimental features; false otherwise. */
  public isApprovalRequired = false;

  /** Whether feature is currently approved.  (Experimental features only.) */
  public isApproved: boolean | undefined;

  /** User id who added approval.  (Experimental features only.) */
  public approvedBy: string | undefined;

  /** Date when approval added to feature.  (Experimental features only.) */
  public approvedDate: Date | undefined;

  /** Whether the default value applies.  For example, permissioning toggles do not have default values. */
  public isDefaultValueApplicable = false;

  /** If default value is applicable, then the current default value; otherwise undefined. */
  public defaultValue: boolean | undefined;

  /** Whether user and group exception apply.  For example, permissioning toggles do not have exceptions. */
  public areExceptionsApplicable = false;

  /** If exceptions are applicable, array of user exceptions as user ids; otherwise undefined. */
  public userExceptions: string[] | undefined;

  /** If exceptions are applicable, array of group exceptions as group ids; otherwise undefined. */
  public groupExceptions: string[] | undefined;

  /** Optional positive integer representing the revision of the feature definition. */
  public revision: number | undefined;

  /** Whether feature is currently active.  Inactive features are never enabled for anyone. */
  public isActive = false;

  /** Whether feature is enabled for the current user. */
  public isEnabledForCurrentUser = false;

  /** Can the current user approve or revoke this feature. */
  public canChangeApproval = false;

  /** Can the current user add a release toggle to this feature. */
  public canAddReleaseToggle = false;

  /** Can the current user activate or deactivate this feature. */
  public canChangeActivation = false;

  /** Can the current user modify the default value. */
  public canChangeDefaultValue = false;

  /** Can the current user add or remove user and group exceptions. */
  public canChangeExceptions = false;

  /** Can the current user reset a feature toggle to its bootstrapped state. */
  public canResetToggle = false;

  /** Can the current user delete a feature toggle, which may result in deleting the entire feature. */
  public canDeleteToggle = false;

  /** Can the current user change the enabled state for themselves. */
  public canChangeEnabledStateForCurrentUser = false;

  /**
   * Creates a new writable non-authoritative Feature instance with the given properties.
   * @param featureId Desired id for the feature.
   * @param properties Optional properties with which to initialize the feature.  Any unspecified
   * properties will be assigned default values.  Note that these properties are assigned and are
   * not deep-copied.
   * @example
   * ```
   * async function onCreateFeatureOnTheFlyButtonPress(event) {
   *   const { featureId, description, issueTracking, productId } = event.data;
   *
   *   // update model to reflect the expected feature state
   *   setProperty(`/features/${idx}`,
   *     new Feature(featureId, { description, issueTracking, productId }));
   *
   *   // call the action method
   *   const authoritativeFeature = await Feature.createFeatureOnTheFly(
   *     featureId, description, issueTracking, productId);
   *
   *   // update model to reflect the new authoritative feature state
   *   setProperty(`/features/${idx}`, authoritativeFeature);
   * }
   * ```
   */
  public constructor(featureId: string, properties?: Partial<Feature>) {
    // assign defaults
    this.id = featureId;
    this.label = featureId;
    this.toggleTypes = [ToggleType.None];
    this.issueTracking = [];
    this.products = [];

    // override properties
    Object.assign(this, properties);
  }

  /**
   * Freezes a feature making it read-only.  This is typically used in the featureFlags API when
   * acquiring authoritative feature objects from the service.  These objects are frozen to prevent
   * copying objects from the features cache.
   * @returns `this`, but as a read-only feature.
   */
  public freeze(): ReadonlyFeature {
    Object.freeze(this.toggleTypes);
    if (this.creationDate) {
      Object.freeze(this.creationDate);
    }
    Object.freeze(this.issueTracking);
    Object.freeze(this.products);
    this.products.forEach((product) => Object.freeze(product));
    if (this.userExceptions) {
      Object.freeze(this.userExceptions);
    }
    if (this.groupExceptions) {
      Object.freeze(this.groupExceptions);
    }

    return Object.freeze(this);
  }

  /**
   * Deep-copies a feature into a writable object.  This is typically used by the client when a
   * simply feature change should be represented by the UI in response to a user event, but the
   * action hasn't yet been completed by the service.
   * @param properties Optional properties with which to override in the new feature.  Note that
   * these properties are assigned and are not deep-copied.
   * @returns Writable feature with equivalent property values.
   * @example
   * ```
   * async function onFeatureEnableButtonPress(event) {
   *   const { featureIdx, feature } = event.data;
   *
   *   // update model to reflect the expected feature state
   *   setProperty(`/features/${idx}`, feature.clone({ isEnabledForCurrentUser: true }));
   *
   *   // call the action method
   *   const authoritativeFeature = await feature.enableForCurrentUser();
   *
   *   // update model to reflect the new authoritative feature state
   *   setProperty(`/features/${idx}`, authoritativeFeature);
   * }
   * ```
   */
  public clone(properties?: Partial<Feature>): Feature {
    const feature = new Feature(this.id, this);

    // deep-copy arrays and objects
    feature.toggleTypes = [...this.toggleTypes]; // string[]
    if (this.creationDate) {
      feature.creationDate = new Date(this.creationDate); // Date
    }
    feature.issueTracking = [...this.issueTracking]; // string[]
    feature.products = this.products.map((product) => Object.assign({}, product)); // Product[]
    if (this.userExceptions) {
      feature.userExceptions = [...this.userExceptions]; // string[]
    }
    if (this.groupExceptions) {
      feature.groupExceptions = [...this.groupExceptions]; // string[]
    }

    // override properties
    Object.assign(feature, properties);

    return feature;
  }

  /**
   * Approves an experimental feature for further use, if applicable.
   * Experimental features must be approved before an environment may use them.
   * If 'canChangeApproval' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link approveFeature}
   */
  public async approve(): Promise<ReadonlyFeature> {
    return approveFeature(this.id);
  }

  /**
   * Revokes (and therefore disables) an experimental feature, if applicable.
   * Experimental features must retain approval while an environment uses them.
   * If 'canChangeApproval' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link revokeFeature}
   */
  public async revoke(): Promise<ReadonlyFeature> {
    return revokeFeature(this.id);
  }

  /**
   * Adds a release toggle to the feature, if applicable.
   * Typically permissioning features can have added release toggles.
   * If 'canAddReleaseToggle' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link addFeatureReleaseToggle}
   */
  public async addReleaseToggle(): Promise<ReadonlyFeature> {
    return addFeatureReleaseToggle(this.id);
  }

  /**
   * Activates a feature, if applicable.
   * If 'canChangeActivation' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link activateFeature}
   */
  public async activate(): Promise<ReadonlyFeature> {
    return activateFeature(this.id);
  }

  /**
   * Dectivates a feature, if applicable.
   * If 'canChangeActivation' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link dectivateFeature}
   */
  public async deactivate(): Promise<ReadonlyFeature> {
    return deactivateFeature(this.id);
  }

  /**
   * Sets the feature's default value, if applicable.
   * If 'canChangeDefaultValue' is false, the returned Promise is likely to be rejected.
   * @param defaultValue New value of the default value.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link setFeatureDefaultValue}
   */
  public async setDefaultValue(defaultValue: boolean): Promise<ReadonlyFeature> {
    return setFeatureDefaultValue(this.id, defaultValue);
  }

  /**
   * Adds a user exception to the feature, if applicable.
   * If 'canChangeExceptions' is false, the returned Promise is likely to be rejected.
   * @param userId Id of user for which to add an exception.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link addFeatureUserException}
   */
  public async addUserException(userId: string): Promise<ReadonlyFeature> {
    return addFeatureUserException(this.id, userId);
  }

  /**
   * Adds a group exception to the feature, if applicable.
   * If 'canChangeExceptions' is false, the returned Promise is likely to be rejected.
   * @param groupId Id of group for which to add an exception.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link addFeatureGroupException}
   */
  public async addGroupException(groupId: string): Promise<ReadonlyFeature> {
    return addFeatureGroupException(this.id, groupId);
  }

  /**
   * Removes a user exception from the feature, if applicable.
   * If 'canChangeExceptions' is false, the returned Promise is likely to be rejected.
   * @param userId Id of user for which to remove an exception.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link removeFeatureUserException}
   */
  public async removeUserException(userId: string): Promise<ReadonlyFeature> {
    return removeFeatureUserException(this.id, userId);
  }

  /**
   * Removes a group exception from the feature, if applicable.
   * If 'canChangeExceptions' is false, the returned Promise is likely to be rejected.
   * @param groupId Id of group for which to remove an exception.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link removeFeatureGroupException}
   */
  public async removeGroupException(groupId: string): Promise<ReadonlyFeature> {
    return removeFeatureGroupException(this.id, groupId);
  }

  /**
   * Resets a feature toggle, if applicable.
   * If 'canResetToggle' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link resetFeatureToggle}
   */
  public async resetToggle(): Promise<ReadonlyFeature> {
    return resetFeatureToggle(this.id);
  }

  /**
   * Deletes a feature toggle, if applicable.  Depending on the feature, this may delete
   * just a feature strategy, or it may delete the entire feature.
   * If 'canDeleteToggle' is false, the returned Promise is likely to be rejected.
   * @returns On success, either the new feature definition should the feature
   * still exist, or undefined if the feature no longer exists; otherwise the promise is rejected
   * with a DetailsError type
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link deleteFeatureToggle}
   */
  public async deleteToggle(): Promise<ReadonlyFeature | undefined> {
    return deleteFeatureToggle(this.id);
  }

  /**
   * Enables the feature for the current user.
   * If 'canChangeEnabledStateForCurrentUser' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link enableFeatureForCurrentUser}
   */
  public async enableForCurrentUser(): Promise<ReadonlyFeature> {
    return enableFeatureForCurrentUser(this.id);
  }

  /**
   * Disables the feature for the current user.
   * If 'canChangeEnabledStateForCurrentUser' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link disableFeatureForCurrentUser}
   */
  public async disableForCurrentUser(): Promise<ReadonlyFeature> {
    return disableFeatureForCurrentUser(this.id);
  }

  /**
   * Toggles the feature for the current user.
   * If 'canChangeEnabledStateForCurrentUser' is false, the returned Promise is likely to be rejected.
   * @returns The new feature definition on success; otherwise the promise is rejected with a
   * DetailsError type.
   * On success, do not keep the original feature definition, as it'll be out-of-date.
   * @see {@link toggleFeatureForCurrentUser}
   */
  public async toggleForCurrentUser(): Promise<ReadonlyFeature> {
    return toggleFeatureForCurrentUser(this.id);
  }
}

/**
 * Read-only array of Products, which are also read-only.
 * @see {@link Product}
 */
export type ReadonlyProducts = ReadonlyArray<Readonly<Product>>;

/**
 * Read-only version of the Feature class, typically denoting an authoritative feature from the
 * service.
 * @see {@link Feature}
 */
export type ReadonlyFeature = {
  readonly id: string;
  readonly idLabel: string | undefined;
  readonly label: string;
  readonly description: string | undefined;

  readonly toggleTypes: ReadonlyArray<ToggleType | string>;

  readonly createdBy: string | undefined;
  readonly creationDate: Readonly<Date> | undefined;
  readonly issueTracking: ReadonlyArray<string>;
  readonly products: ReadonlyProducts;

  readonly isDynamicallyAddedToggle: boolean;
  readonly isApprovalRequired: boolean;
  readonly isApproved: boolean | undefined;
  readonly approvedBy: string | undefined;
  readonly approvedDate: Readonly<Date> | undefined;
  readonly isDefaultValueApplicable: boolean;
  readonly defaultValue: boolean | undefined;
  readonly areExceptionsApplicable: boolean;
  readonly userExceptions: ReadonlyArray<string> | undefined;
  readonly groupExceptions: ReadonlyArray<string> | undefined;

  readonly revision: number | undefined;
  readonly isActive: boolean;

  readonly isEnabledForCurrentUser: boolean;

  readonly canChangeApproval: boolean;
  readonly canAddReleaseToggle: boolean;
  readonly canChangeActivation: boolean;
  readonly canChangeDefaultValue: boolean;
  readonly canChangeExceptions: boolean;
  readonly canResetToggle: boolean;
  readonly canDeleteToggle: boolean;
  readonly canChangeEnabledStateForCurrentUser: boolean;
} & Pick<
  Feature,
  {
    [Key in keyof Feature]: Feature[Key] extends (...args: any) => any ? Key : never;
  }[keyof Feature]
>;
